import { useRef, useEffect, useCallback } from "react";

const DEFAULT_CHATBOT_WS_URL = process.env.REACT_APP_CHATBOT_WS_URL || 'wss://chatbot-215277898238.us-central1.run.app/ws/chatbot';

const buildChatbotUrl = (baseUrl, scenarioId, conversationId, extraParams = {}) => {
    const url = new URL(baseUrl);
    if (scenarioId) url.searchParams.set('id', scenarioId);
    if (conversationId) url.searchParams.set('conversation_id', conversationId);
    Object.entries(extraParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) url.searchParams.set(key, value);
    });
    return url.toString();
};

const useChatbotRelay = ({ wsUrl = DEFAULT_CHATBOT_WS_URL, onRawMessage, onBotTyping }) => {
    const sessions = useRef(new Map());
    const onRawRef = useRef(onRawMessage);
    const onTypeRef = useRef(onBotTyping);

    useEffect(() => { onRawRef.current = onRawMessage; });
    useEffect(() => { onTypeRef.current = onBotTyping; });

    const openConnection = useCallback((chatId, scenarioId, conversationId = null, extraParams = {}) => {
        const existing = sessions.current.get(chatId);
        if (existing && existing.socket.readyState !== WebSocket.CLOSED) return;
        
        const url = buildChatbotUrl(wsUrl, scenarioId, conversationId, extraParams)
        console.log('[useChatbotRelay] 🚀 ', JSON.stringify(url, null, 2));
        const socket = new WebSocket(url);
        const session = { socket, pendingSends: [] };
        sessions.current.set(chatId, session);

        socket.onopen = () => {
            session.pendingSends.splice(0).forEach(payload => socket.send(JSON.stringify(payload)));
        };

        socket.onmessage = (event) => {
            onTypeRef.current?.(chatId, false);
            onRawRef.current?.(chatId, event.data);
        };

        socket.onerror = (event) => {
            console.error('[ChatbotRelay] WebSocket error:', event);
            onTypeRef.current?.(chatId, false);
        };

        socket.onclose = (event) => {
            sessions.current.delete(chatId);
            onTypeRef.current?.(chatId, false);
            if (!event.wasClean) {
                console.error(`[ChatbotRelay] WebSocket closed unexpectedly: ${event.code} ${event.reason || ''}`);
            }
        };
    }, [wsUrl]);

    const closeConnection = useCallback((chatId) => {
        const session = sessions.current.get(chatId);
        sessions.current.delete(chatId);
        if (!session) return;
        if (session.socket.readyState === WebSocket.OPEN || session.socket.readyState === WebSocket.CONNECTING) {
            session.socket.close(1000, 'chat closed');
        }
    }, []);

    const send = useCallback((chatId, payload) => {
        const session = sessions.current.get(chatId);
        if (!session) return false;
        if (session.socket.readyState === WebSocket.OPEN) {
            session.socket.send(JSON.stringify(payload));
            return true;
        }
        if (session.socket.readyState === WebSocket.CONNECTING) {
            session.pendingSends.push(payload);
            return true;
        }
        return false;
    }, []);

    useEffect(() => {
        return () => {
            sessions.current.forEach(({ socket }) => {
                if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
                    socket.close(1000, 'provider unmounted');
                }
            });
            sessions.current.clear();
        };
    }, []);

    return { openConnection, closeConnection, send };
};

export default useChatbotRelay;
